import { Component, ElementRef, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SecurityService } from '../../security/services/security.service';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { APP_ROUTES } from 'src/app/app-routing.const';
import { notify } from '../../utils/function/notification.util';
import { NotificationService } from '../../notifications/notification.service';
import { NotificationType } from '../../notifications/notification.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent {

  APP_ROUTES = APP_ROUTES;

  cleartext: boolean = false;
  isLoggingIn: boolean = false;

  loginForm: FormGroup;

  // MFA variables
  mfaRequired: boolean = false;
  otpDigits: string[] = ['', '', '', '', '', ''];
  loginEmail: string = '';
  isVerifyingMfa: boolean = false;

  //MFA setup
  mfaSetupRequired: boolean = false;
  setupOtpDigits: string[] = ['', '', '', '', '', ''];
  isLoadingSetup: boolean = false;
  isVerifyingSetup: boolean = false;
  isSetupSuccess: boolean = false;
  qrCodeUri: string | null = null;
  manualSecret: string | null = null;

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('setupOtpInput') setupOtpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  constructor(private formBuilder: FormBuilder,
              private securityService: SecurityService,
              private router: Router,
              private notificationService: NotificationService,
              private translateService: TranslateService
  ) {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });
  }

  get mfaCode(): string {
    return this.otpDigits.join('');
  }

  get setupMfaCode(): string {
    return this.setupOtpDigits.join('');
  }

  login() {
    if (this.loginForm.invalid) {
      console.log('Error: invalid credentials! Unable to login')
      return;
    }
    this.isLoggingIn = true;
    this.loginEmail = this.loginForm.get('email')?.value;

    this.securityService.login(
      this.loginEmail,
      this.loginForm.get('password')?.value
    ).pipe(
      finalize(() => this.isLoggingIn = false),
    ).subscribe({
      next: (response: any) => {
        if (response.mfaSetupRequired) {
          this.mfaSetupRequired = true;
          this.startMfaSetup();
        }
       else if (response.mfaRequired) {
         this.mfaRequired = true;
         this.resetOtp();
         setTimeout(() => this.otpInputs?.first?.nativeElement.focus());
       }else{
        this.router.navigate([APP_ROUTES.DASHBOARD]);
       }
     },
     error: (err) => {
        const message = this.translateService.instant('login.error.connect',{error: err.message});
        notify(this.notificationService,NotificationType.ERROR, message);
     }
   });
  }

  startMfaSetup() {
    this.isLoadingSetup = true;
    this.securityService.setupMfa()
      .pipe(finalize(() => this.isLoadingSetup = false))
      .subscribe({
        next: (response: any) => {
          this.manualSecret = response.mfaCode;
          this.qrCodeUri = response.qrCode;
          setTimeout(() =>this.setupOtpInputs?.first?.nativeElement.focus())
        },
        error: (err) => {
          const message = this.translateService.instant('login.mfa.error', {error: err.message});
          notify(this.notificationService,NotificationType.ERROR, message);
          this.cancelMfaSetup();
        }
      })
  }

  confirmMfaSetup() {
    if (this.setupMfaCode.length !== 6 || this.isVerifyingSetup) return;
    this.isVerifyingSetup = true;

    this.securityService.verifyMfaSetup(this.setupMfaCode)
      .pipe(finalize(() => this.isVerifyingSetup = false))
      .subscribe({
        next: () => {
          this.mfaSetupRequired = false;
          this.isSetupSuccess = true
          this.router.navigate([APP_ROUTES.DASHBOARD]);
        },
        error: (err) => {
          this.isVerifyingSetup = false;
          this.resetSetupOtp();
          this.setupOtpInputs?.first?.nativeElement.focus();
          if (err.status === 429) {
            notify(this.notificationService,NotificationType.WARNING, 'login.mfa.warn.attempts');
          } else {
            notify(this.notificationService,NotificationType.ERROR, 'login.mfa.error.code');
          }
        }
      });
  }

  copySecret(): void {
    if (this.manualSecret) navigator.clipboard.writeText(this.manualSecret);
  }

  cancelMfaSetup() {
    this.mfaSetupRequired = false;
    this.qrCodeUri = null;
    this.manualSecret = null;
    this.resetSetupOtp();
    this.loginForm.reset();
  }

  onSetupOtpInput(event: Event, index: number) {
      const input = event.target as HTMLInputElement;
      const value = input.value.replace(/\D/g, '').slice(-1);
      this.setupOtpDigits[index] = value;
      input.value = value;

      if (value && index < this.setupOtpDigits.length - 1) {
        this.setupOtpInputs.get(index + 1)?.nativeElement.focus();
      }

      if (this.setupMfaCode.length === 6 && !this.isVerifyingSetup) {
        this.confirmMfaSetup();
      }
  }

  onSetupOtpKeydown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !this.setupOtpDigits[index] && index > 0) {
      this.setupOtpInputs.get(index - 1)?.nativeElement.focus();
    }
    if (event.key === 'Enter' && this.setupMfaCode.length === 6) {
      this.confirmMfaSetup();
    }
  }

  onSetupOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const digits = (event.clipboardData?.getData('text').match(/\d/g) ?? []).slice(0, 6);
    digits.forEach((d, i) => this.setupOtpDigits[i] = d);
    const focusIndex = Math.min(digits.length, 5);
    this.setupOtpInputs.get(focusIndex)?.nativeElement.focus();

    if (this.setupMfaCode.length === 6 && !this.isVerifyingSetup) {
      this.confirmMfaSetup();
    }
  }

  private resetSetupOtp() {
    this.setupOtpDigits = ['', '', '', '', '', ''];
  }

  // ---- OTP segmented input handlers ----

  onOtpInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(-1);
    this.otpDigits[index] = value;
    input.value = value;

    if (value && index < this.otpDigits.length - 1) {
      this.otpInputs.get(index + 1)?.nativeElement.focus();
    }

    if (this.mfaCode.length === 6 && !this.isVerifyingMfa) {
      this.verifyMfaCode();
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
      this.otpInputs.get(index - 1)?.nativeElement.focus();
    }
    if (event.key === 'Enter' && this.mfaCode.length === 6) {
      this.verifyMfaCode();
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const digits = (event.clipboardData?.getData('text').match(/\d/g) ?? []).slice(0, 6);
    digits.forEach((d, i) => this.otpDigits[i] = d);
    const focusIndex = Math.min(digits.length, 5);
    this.otpInputs.get(focusIndex)?.nativeElement.focus();

    if (this.mfaCode.length === 6 && !this.isVerifyingMfa) {
      this.verifyMfaCode();
    }
  }

  private resetOtp() {
    this.otpDigits = ['', '', '', '', '', ''];
  }

  // ---- MFA verification ----

  verifyMfaCode() {
    if (this.mfaCode.length !== 6) return;
    this.isVerifyingMfa = true;

    this.securityService.verifyMfa(this.mfaCode)
      .pipe(
        finalize(() => this.isVerifyingMfa = false)
      ).subscribe({
        next: () => {
          this.router.navigate([APP_ROUTES.DASHBOARD]);
        },
        error: (err) => {
          this.resetOtp();
          this.otpInputs?.first?.nativeElement.focus();
          if (err.status === 429) {
            // rate limiter: not an invalid code
            notify(this.notificationService,NotificationType.WARNING, 'login.mfa.warn.attempts');
            // TODO notification: "Veuillez patienter avant de réessayer"
          } else {

            notify(this.notificationService,NotificationType.ERROR, 'login.mfa.error.code');
            // TODO notification: "Code invalide ou délai dépassé — reconnectez-vous"
          }
        }
      });
  }

  cancelMfa() {
    this.mfaRequired = false;
    this.resetOtp();
    this.loginForm.reset();
  }

  togglePassword() {
    this.cleartext = !this.cleartext;
  }

  goToPasswordRecovery() {
    this.router.navigate([APP_ROUTES.AUTH.PASSWORD_RECOVERY]);
  }
}
